import { useContext, useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DesktopTower, DownloadSimple, Shield, Database, UserPlus, PencilSimple, Trash, X, Check, LockKey, LockOpen, ShieldCheck } from "@phosphor-icons/react";
import { AuthContext, API, useProcesses } from "../App";
import axios from "axios";
import { toast } from "sonner";

const ROLES = ["Admin", "Employee", "Worker"];

// ── Section catalogue for Special Access ────────────────────────────
// Keeps the picker human-scannable and grouped like the sidebar.
// Dynamic Operations are appended at runtime from useProcesses().
const STATIC_SECTIONS = [
  { group: "Overview", items: [
    { key: "dashboard", label: "Dashboard" },
    { key: "sales-register", label: "Sales Register" },
    { key: "production-planning", label: "Production Planning" },
  ]},
  { group: "Raw Material", items: [
    { key: "raw-material", label: "RM Entry" },
    { key: "rm-planning", label: "RM Planning" },
    { key: "rm-opening-stock", label: "RM Opening Stock" },
  ]},
  { group: "Reports", items: [
    { key: "final-report", label: "Final Report" },
    { key: "work-order", label: "Work Order" },
    { key: "daily-review", label: "Daily Review Sheet" },
    { key: "inventory", label: "Inventory" },
    { key: "suppliers", label: "Suppliers" },
    { key: "stock-at-supplier", label: "Stock at Supplier" },
  ]},
  { group: "Costing", items: [
    { key: "cost-sheet", label: "Cost Sheet" },
    { key: "budget-schedule", label: "Budget as per Schedule" },
    { key: "performance-dispatch", label: "Performance as per Dispatch" },
  ]},
  { group: "Quality", items: [
    { key: "customer-complaints", label: "Customer Complaints" },
    { key: "quality-dashboard", label: "Quality Dashboard" },
  ]},
  { group: "Masters", items: [
    { key: "part-master", label: "Part Master" },
    { key: "bom-master", label: "BOM Master" },
    { key: "process-master", label: "Process Master" },
    { key: "customer-master", label: "Customer Master" },
    { key: "company-master", label: "Company Master" },
    { key: "machine-master", label: "Machine Master" },
    { key: "bom-routing", label: "BOM Routing" },
    { key: "bought-out", label: "Bought Out" },
    { key: "defect-master", label: "Defect Master" },
  ]},
];

const SFA_PASSWORD = "SFA2026";

export default function Settings() {
  const { user } = useContext(AuthContext);
  const { processes } = useProcesses();
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "Employee" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ username: "", password: "", role: "" });

  // ── Special Feature Access (SFA) state ──────────────────────────────
  const [sfaUnlocked, setSfaUnlocked] = useState(false);
  const [sfaPwdOpen, setSfaPwdOpen] = useState(false);
  const [sfaPwdInput, setSfaPwdInput] = useState("");
  const [sectionsModal, setSectionsModal] = useState(null); // { user, selected: Set }

  const OPERATION_SECTIONS = (processes || []).map(p => ({
    key: `operations/${p.code}`,
    label: p.name || p.code,
  }));
  const ALL_GROUPS = [
    ...STATIC_SECTIONS.slice(0, 2),
    { group: "Operations / Processes", items: OPERATION_SECTIONS },
    ...STATIC_SECTIONS.slice(2),
  ];

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsers(res.data || []);
    } catch { }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) { toast.error("Username and password required"); return; }
    try {
      await axios.post(`${API}/auth/users`, newUser);
      toast.success(`User '${newUser.username}' created`);
      setNewUser({ username: "", password: "", role: "Employee" });
      setShowAdd(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
  };

  const handleUpdateUser = async (id) => {
    const payload = {};
    if (editData.username) payload.username = editData.username;
    if (editData.password) payload.password = editData.password;
    if (editData.role) payload.role = editData.role;
    try {
      await axios.put(`${API}/auth/users/${id}`, payload);
      toast.success("User updated");
      setEditingId(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleDeleteUser = async (id, username) => {
    if (!window.confirm(`Delete user '${username}'?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${id}`);
      toast.success(`User '${username}' deleted`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const submitSfaPassword = () => {
    if (sfaPwdInput === SFA_PASSWORD) {
      setSfaUnlocked(true);
      setSfaPwdOpen(false);
      setSfaPwdInput("");
      toast.success("Special Feature Access unlocked");
    } else {
      toast.error("Wrong password");
    }
  };

  const openSectionsFor = (u) => {
    setSectionsModal({
      user: u,
      selected: new Set(Array.isArray(u.allowed_sections) ? u.allowed_sections : []),
    });
  };

  const toggleSectionKey = (k) => {
    setSectionsModal(m => {
      const s = new Set(m.selected);
      if (s.has(k)) s.delete(k); else s.add(k);
      return { ...m, selected: s };
    });
  };

  const saveSections = async () => {
    if (!sectionsModal) return;
    const list = Array.from(sectionsModal.selected);
    try {
      await axios.put(`${API}/auth/users/${sectionsModal.user.id}`, { allowed_sections: list });
      toast.success(`Sections saved for '${sectionsModal.user.username}'`);
      setSectionsModal(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save sections");
    }
  };

  const clearSections = async () => {
    if (!sectionsModal) return;
    if (!window.confirm(`Give '${sectionsModal.user.username}' access to EVERYTHING (clear restrictions)?`)) return;
    try {
      await axios.put(`${API}/auth/users/${sectionsModal.user.id}`, { allowed_sections: [] });
      toast.success("Restrictions cleared — user sees everything");
      setSectionsModal(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Settings</h1>
          <p className="text-sm text-[#64748B] mt-1">Creator panel &mdash; User management, downloads &amp; configuration</p>
        </div>

        {/* User Management */}
        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="user-management-section">
          <div className="bg-[#1D2951] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-[#F5DF4D]" />
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">User Management</h2>
              <span className="bg-[#F5DF4D] text-[#1D2951] text-xs font-bold rounded-full px-2 py-0.5">{users.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => sfaUnlocked ? setSfaUnlocked(false) : setSfaPwdOpen(true)}
                className={`text-xs font-bold gap-1 h-8 ${sfaUnlocked ? "bg-[#059669] hover:bg-[#047857] text-white" : "bg-[#B45309] hover:bg-[#92400E] text-white"}`}
                data-testid="sfa-toggle-btn">
                {sfaUnlocked ? <><LockOpen size={14} /> Special Access · Unlocked</> : <><LockKey size={14} /> Access Special Features</>}
              </Button>
              <Button size="sm" onClick={() => setShowAdd(!showAdd)}
                className="bg-[#F5DF4D] hover:bg-[#EAD63A] text-[#1D2951] text-xs font-bold gap-1 h-8" data-testid="add-user-btn">
                <UserPlus size={14} /> Add User
              </Button>
            </div>
          </div>

          {/* Add User Form */}
          {showAdd && (
            <div className="p-4 bg-[#F0FDF4] border-b border-[#BBF7D0] flex items-end gap-3" data-testid="add-user-form">
              <div className="flex-1">
                <Label className="text-[11px] font-semibold">Username</Label>
                <Input value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Enter username" className="min-h-[36px] text-sm" data-testid="new-username" />
              </div>
              <div className="flex-1">
                <Label className="text-[11px] font-semibold">Password</Label>
                <Input value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Enter password" className="min-h-[36px] text-sm" data-testid="new-password" />
              </div>
              <div className="w-40">
                <Label className="text-[11px] font-semibold">Role</Label>
                <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger className="min-h-[36px] text-sm" data-testid="new-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddUser} className="bg-[#059669] hover:bg-[#047857] text-white h-9 px-4 text-xs font-bold" data-testid="save-new-user">
                <Check size={14} className="mr-1" /> Save
              </Button>
              <Button variant="outline" onClick={() => setShowAdd(false)} className="h-9 px-3 text-xs">
                <X size={14} />
              </Button>
            </div>
          )}

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="bg-[#F1F5F9] text-xs uppercase tracking-wider text-[#64748B]">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Username</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  {sfaUnlocked && <th className="px-4 py-2 text-left">Sections Access</th>}
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr key={u.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-2.5 text-[#94A3B8] text-xs">{idx + 1}</td>
                    <td className="px-4 py-2.5">
                      {editingId === u.id ? (
                        <Input value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })}
                          className="min-h-[32px] text-sm w-40" data-testid="edit-username" />
                      ) : (
                        <span className="font-bold text-[#1D2951]">{u.username}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {editingId === u.id ? (
                        <Select value={editData.role} onValueChange={v => setEditData({ ...editData, role: v })}>
                          <SelectTrigger className="min-h-[32px] text-sm w-32" data-testid="edit-role"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          u.role === "Creator" ? "bg-[#7C3AED] text-white" :
                          u.role === "Admin" ? "bg-[#1D2951] text-[#F5DF4D]" :
                          "bg-[#E2E8F0] text-[#475569]"
                        }`}>{u.role}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#64748B]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                    </td>
                    {sfaUnlocked && (
                      <td className="px-4 py-2.5">
                        {u.role === "Creator" || u.role === "Admin" ? (
                          <span className="text-[10px] text-[#64748B] italic">Full access (role)</span>
                        ) : Array.isArray(u.allowed_sections) && u.allowed_sections.length > 0 ? (
                          <button
                            onClick={() => openSectionsFor(u)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF] text-[11px] font-bold hover:bg-[#DBEAFE]"
                            data-testid={`sections-${u.username}`}
                          >
                            <ShieldCheck size={12} /> {u.allowed_sections.length} section{u.allowed_sections.length > 1 ? 's' : ''} · Edit
                          </button>
                        ) : (
                          <button
                            onClick={() => openSectionsFor(u)}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534] text-[11px] font-bold hover:bg-[#DCFCE7]"
                            data-testid={`sections-${u.username}`}
                          >
                            <LockOpen size={12} /> Full · Restrict
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-center">
                      {u.role === "Creator" ? (
                        <span className="text-xs text-[#94A3B8]">Protected</span>
                      ) : editingId === u.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <div className="flex-shrink-0">
                            <Label className="text-xs text-[#64748B]">New Password</Label>
                            <Input value={editData.password} onChange={e => setEditData({ ...editData, password: e.target.value })}
                              placeholder="Leave blank to keep" className="min-h-[32px] text-xs w-32" data-testid="edit-password" />
                          </div>
                          <Button size="sm" onClick={() => handleUpdateUser(u.id)}
                            className="bg-[#059669] hover:bg-[#047857] text-white h-7 px-2 text-xs mt-4" data-testid="save-edit-user">
                            <Check size={12} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 px-2 text-xs mt-4">
                            <X size={12} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => {
                            setEditingId(u.id);
                            setEditData({ username: u.username, password: "", role: u.role });
                          }} className="h-7 px-2 text-xs" data-testid={`edit-user-${u.username}`}>
                            <PencilSimple size={12} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteUser(u.id, u.username)}
                            className="h-7 px-2 text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-user-${u.username}`}>
                            <Trash size={12} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── Special Feature Access · password-gate modal ── */}
        <Dialog open={sfaPwdOpen} onOpenChange={setSfaPwdOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#B45309]">
                <LockKey size={20} /> Special Feature Access
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#64748B] -mt-2">
              Enter the special-access password to unlock the ability to assign
              specific sidebar sections to non-admin users.
            </p>
            <Input
              type="password"
              value={sfaPwdInput}
              onChange={e => setSfaPwdInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitSfaPassword()}
              placeholder="Password"
              className="min-h-[40px]"
              autoFocus
              data-testid="sfa-password-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSfaPwdOpen(false)} className="h-9">Cancel</Button>
              <Button onClick={submitSfaPassword}
                className="bg-[#B45309] hover:bg-[#92400E] text-white h-9"
                data-testid="sfa-password-submit">
                <LockOpen size={14} className="mr-1" /> Unlock
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Section picker · per-user ── */}
        <Dialog open={!!sectionsModal} onOpenChange={(o) => !o && setSectionsModal(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-[#B45309]" />
                Sections Access — <span className="text-[#1D2951]">{sectionsModal?.user?.username}</span>
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#64748B] -mt-2">
              Tick the sections this user should see in their sidebar. Untick to hide.
              Leave everything unticked and press <b>Give Full Access</b> to remove all restrictions.
            </p>

            {sectionsModal && (
              <div className="space-y-4">
                {ALL_GROUPS.map(g => (
                  <div key={g.group} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <div className="bg-[#F1F5F9] px-3 py-1.5 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#1D2951]">{g.group}</span>
                      <div className="flex gap-2">
                        <button className="text-[10px] text-[#059669] font-bold hover:underline"
                          onClick={() => setSectionsModal(m => {
                            const s = new Set(m.selected);
                            g.items.forEach(it => s.add(it.key));
                            return { ...m, selected: s };
                          })}>Select all</button>
                        <button className="text-[10px] text-[#DC2626] font-bold hover:underline"
                          onClick={() => setSectionsModal(m => {
                            const s = new Set(m.selected);
                            g.items.forEach(it => s.delete(it.key));
                            return { ...m, selected: s };
                          })}>Clear</button>
                      </div>
                    </div>
                    <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {g.items.length === 0 && <span className="text-[11px] text-[#94A3B8] italic col-span-3">No items configured yet</span>}
                      {g.items.map(it => {
                        const on = sectionsModal.selected.has(it.key);
                        return (
                          <label key={it.key}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded border cursor-pointer text-xs ${on ? 'bg-[#EFF6FF] border-[#BFDBFE] text-[#1E40AF]' : 'bg-white border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]'}`}
                            data-testid={`section-chip-${it.key}`}>
                            <input type="checkbox" checked={on}
                              onChange={() => toggleSectionKey(it.key)} className="accent-[#1D2951]" />
                            <span className="truncate font-semibold">{it.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2 border-t border-[#E2E8F0] mt-4">
              <Button variant="outline" onClick={clearSections} className="h-9 text-[#059669] border-[#059669]">
                <LockOpen size={14} className="mr-1" /> Give Full Access
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setSectionsModal(null)} className="h-9">Cancel</Button>
                <Button onClick={saveSections}
                  className="bg-[#1D2951] hover:bg-[#2A3B73] text-white h-9"
                  data-testid="sections-save">
                  <Check size={14} className="mr-1" /> Save Access
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="downloads-section">
          <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
            <DownloadSimple size={18} className="text-[#F5DF4D]" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Downloads</h2>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#1D2951] rounded-lg flex items-center justify-center">
                  <DesktopTower size={24} className="text-[#F5DF4D]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#1D2951]">TAC-PT Desktop App</h3>
                  <p className="text-[11px] text-[#64748B] mt-0.5">Windows (.exe) &bull; Electron &bull; v1.0.0</p>
                </div>
              </div>
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px] px-5 text-xs font-bold gap-2" data-testid="download-electron-btn"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = `${process.env.REACT_APP_BACKEND_URL}/TAC-PT-Builder.tar.gz`;
                  a.download = 'TAC-PT-Builder.tar.gz';
                  a.click();
                }}>
                <DownloadSimple size={16} /> Download Builder
              </Button>
            </div>
            <div className="p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg text-[11px] text-[#92400E] space-y-1">
              <p className="font-bold">How to build the .exe:</p>
              <ol className="list-decimal pl-4 space-y-0.5">
                <li>Download &amp; extract the .tar.gz file</li>
                <li>Make sure <strong>Node.js</strong> is installed on your Windows PC</li>
                <li>Double-click <strong>BUILD.bat</strong></li>
                <li>Find <strong>TAC-PT Setup.exe</strong> in the <code className="bg-[#FEF3C7] px-1 rounded">dist/</code> folder</li>
              </ol>
            </div>
          </div>
        </Card>

        {/* Data Management */}
        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="data-section">
          <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
            <Database size={18} className="text-[#F5DF4D]" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Data Management</h2>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg">
              <h3 className="text-sm font-bold text-[#059669]">Backup</h3>
              <p className="text-[11px] text-[#64748B] mt-1">Export all data as a downloadable backup file</p>
              <Button variant="outline" className="mt-3 text-xs border-[#059669] text-[#059669]" disabled>Coming Soon</Button>
            </div>
            <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg">
              <h3 className="text-sm font-bold text-[#DC2626]">Restore</h3>
              <p className="text-[11px] text-[#64748B] mt-1">Restore from backup file</p>
              <Button variant="outline" className="mt-3 text-xs border-[#DC2626] text-[#DC2626]" disabled>Coming Soon</Button>
            </div>
          </div>
        </Card>

        {/* Account */}
        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="account-section">
          <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
            <Shield size={18} className="text-[#F5DF4D]" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Account</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <p className="text-[#94A3B8]">Username</p>
                <p className="font-bold text-[#1D2951] mt-0.5">{user?.username}</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Role</p>
                <p className="font-bold text-white bg-[#7C3AED] inline-block px-2 py-0.5 rounded mt-0.5">{user?.role}</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">App Version</p>
                <p className="font-bold text-[#1D2951] mt-0.5">v1.0.0</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Environment</p>
                <p className="font-bold text-[#059669] mt-0.5">Cloud</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
